import { HashRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider, ProtectedRoute } from './hooks/useAuth'
import { ThemeProvider } from './hooks/useTheme'

// Pages
import LoginPage from './pages/LoginPage'
import DashboardPage from './pages/DashboardPage'
import ClientListPage from './pages/clients/ClientListPage'
import ClientDetailPage from './pages/clients/ClientDetailPage'
import ClientEditPage from './pages/clients/ClientEditPage'
import ClientStatsPage from './pages/clients/ClientStatsPage'
import ExerciseListPage from './pages/exercises/ExerciseListPage'
import WorkoutListPage from './pages/workouts/WorkoutListPage'
import WorkoutDetailPage from './pages/workouts/WorkoutDetailPage'
import KnowledgeListPage from './pages/knowledge/KnowledgeListPage'
import KnowledgeEditPage from './pages/knowledge/KnowledgeEditPage'
import MealPlanListPage from './pages/meals/MealPlanListPage'
import MealPlanDetailPage from './pages/meals/MealPlanDetailPage'
import SchedulePage from './pages/schedule/SchedulePage'
import ChatPage from './pages/ChatPage'
import VoiceChatPage from './pages/VoiceChatPage'
import ProfilePage from './pages/ProfilePage'
import ChatActivityPage from './pages/ChatActivityPage'
import ClientChatsPage from './pages/client-chats/ClientChatsPage'
import ClientChatViewPage from './pages/client-chats/ClientChatViewPage'
import FeatureRequestsPage from './pages/feature-requests/FeatureRequestsPage'
import AISettingsPage from './pages/ai-settings/AISettingsPage'
import MemoryLogPage from './pages/MemoryLogPage'
import ReleaseLogPage from './pages/ReleaseLogPage'
import AnnouncementsPage from './pages/AnnouncementsPage'
import BillingPage from './pages/billing/BillingPage'
import PackageDetailPage from './pages/billing/PackageDetailPage'
import SubscriptionPlansPage from './pages/billing/SubscriptionPlansPage'
import EscalationToast from './components/EscalationToast'
import { ErrorBoundary } from './components/ui'

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
})

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />

      {/* Protected routes */}
      <Route path="/" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />

      {/* Clients */}
      <Route path="/clients" element={<ProtectedRoute><ClientListPage /></ProtectedRoute>} />
      <Route path="/clients/new" element={<ProtectedRoute><ClientEditPage /></ProtectedRoute>} />
      <Route path="/clients/:id" element={<ProtectedRoute><ClientDetailPage /></ProtectedRoute>} />
      <Route path="/clients/:id/edit" element={<ProtectedRoute><ClientEditPage /></ProtectedRoute>} />
      <Route path="/clients/:id/stats" element={<ProtectedRoute><ClientStatsPage /></ProtectedRoute>} />

      {/* Exercises */}
      <Route path="/exercises" element={<ProtectedRoute><ExerciseListPage /></ProtectedRoute>} />

      {/* Workouts */}
      <Route path="/workouts" element={<ProtectedRoute><WorkoutListPage /></ProtectedRoute>} />
      <Route path="/workouts/new" element={<ProtectedRoute><WorkoutDetailPage /></ProtectedRoute>} />
      <Route path="/workouts/:id" element={<ProtectedRoute><WorkoutDetailPage /></ProtectedRoute>} />

      {/* Knowledge */}
      <Route path="/knowledge" element={<ProtectedRoute><KnowledgeListPage /></ProtectedRoute>} />
      <Route path="/knowledge/new" element={<ProtectedRoute><KnowledgeEditPage /></ProtectedRoute>} />
      <Route path="/knowledge/:id/edit" element={<ProtectedRoute><KnowledgeEditPage /></ProtectedRoute>} />

      {/* Meal Plans */}
      <Route path="/meals" element={<ProtectedRoute><MealPlanListPage /></ProtectedRoute>} />
      <Route path="/meals/new" element={<ProtectedRoute><MealPlanDetailPage /></ProtectedRoute>} />
      <Route path="/meals/:id" element={<ProtectedRoute><MealPlanDetailPage /></ProtectedRoute>} />

      {/* Schedule */}
      <Route path="/schedule" element={<ProtectedRoute><SchedulePage /></ProtectedRoute>} />

      {/* Chat */}
      <Route path="/chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
      <Route path="/voice-chat" element={<ProtectedRoute><VoiceChatPage /></ProtectedRoute>} />

      {/* Chat Activity & Client Chats */}
      <Route path="/chat-activity" element={<ProtectedRoute><ChatActivityPage /></ProtectedRoute>} />
      <Route path="/client-chats" element={<ProtectedRoute><ClientChatsPage /></ProtectedRoute>} />
      <Route path="/client-chats/:id" element={<ProtectedRoute><ClientChatViewPage /></ProtectedRoute>} />

      {/* Profile */}
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />

      {/* Feature Requests */}
      <Route path="/feature-requests" element={<ProtectedRoute><FeatureRequestsPage /></ProtectedRoute>} />

      {/* AI Settings (Tasks & Directives) */}
      <Route path="/ai-settings" element={<ProtectedRoute><AISettingsPage /></ProtectedRoute>} />

      {/* Memory Log */}
      <Route path="/memory-log" element={<ProtectedRoute><MemoryLogPage /></ProtectedRoute>} />

      {/* Billing */}
      <Route path="/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
      <Route path="/billing/subscriptions" element={<ProtectedRoute><SubscriptionPlansPage /></ProtectedRoute>} />
      <Route path="/billing/:id" element={<ProtectedRoute><PackageDetailPage /></ProtectedRoute>} />

      {/* Release Log */}
      <Route path="/release-log" element={<ProtectedRoute><ReleaseLogPage /></ProtectedRoute>} />

      {/* Announcements / Broadcast */}
      <Route path="/announcements" element={<ProtectedRoute><AnnouncementsPage /></ProtectedRoute>} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <HashRouter>
          <AuthProvider>
            <ErrorBoundary>
              <AppRoutes />
              <EscalationToast />
            </ErrorBoundary>
          </AuthProvider>
        </HashRouter>
      </ThemeProvider>
    </QueryClientProvider>
  )
}
